import {
    getCurrentValue,
    OtherInvestmentFinancialEntity,
} from '../../financialEntities';
import { calculateForecastAmount } from '../../forecasting';
import { AmountRecurrence } from '../../types';
import { dt } from '../../util/dateTime';
import { isNumber } from '../../util/types';
import { makeValueSeriesGenerator } from '../makeGenerator';
import { endOfTickDateStr } from '../ticks';
import { FEValueNode, SnapshotBase, VSGContext } from '../types';
import {
    makeDefaultCurrentValueNodeGenerator,
    makeDefaultPastValueNodeGenerator,
} from './helpers';

type Params = {
    balance?: number;
    expectedReturn?: number;
    expectedContribution?: AmountRecurrence;
};

// N.B. This logic is currently identical to the BrokerageAccount VSG
export const makeOtherInvestmentValueSeriesGenerator = (
    entity: OtherInvestmentFinancialEntity,
    snapshots: SnapshotBase[],
    ctx: VSGContext,
    params: Params = {}
) => {
    const currentValue = isNumber(params.balance)
        ? params.balance
        : getCurrentValue(entity) ?? 0;
    const annualGrowthRate = isNumber(params.expectedReturn)
        ? params.expectedReturn
        : entity.info.expectedReturn ?? 0;
    const startDate = dt(ctx.currentDate).toDateStr();
    const amountRecurrence = params.expectedContribution ??
        entity.info.expectedContribution ?? {
            amount: 0,
            recurrence: { freq: 'MONTHLY', interval: 1 },
        };
    const vnParams = { ...ctx.baseValueNodeParams };

    return makeValueSeriesGenerator<SnapshotBase, FEValueNode>({
        snapshots,
        currentDate: ctx.currentDate,
        generatePastValueNode: makeDefaultPastValueNodeGenerator<SnapshotBase>({
            ...vnParams,
        }),
        generateCurrentValueNode:
            makeDefaultCurrentValueNodeGenerator<SnapshotBase>({
                currentValue,
                ...vnParams,
            }),
        generateFutureValueNode: ({ tick }) => {
            const balance = calculateForecastAmount({
                startAmount: currentValue,
                annualGrowthRate,
                transactionSchedulePlans: [{ ...amountRecurrence, startDate }],
                startDate,
                targetDate: endOfTickDateStr(tick),
            });
            return { ...tick, balance, ...vnParams };
        },
    });
};
